/** Canonical product modal. Default choice for overlays — use dialog.tsx only when you need headless content without this chrome. */
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Loader2Icon, XIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'

import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog'

interface CustomDialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  showOverlay?: boolean
}

const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  CustomDialogContentProps
>(({ className, children, showOverlay = true, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-[2100] overflow-y-auto',
        showOverlay ? 'bg-black/45' : 'bg-transparent',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      )}
    >
      <div className='flex min-h-full items-center justify-center p-4'>
        <DialogPrimitive.Content
          ref={ref}
          className={cn('relative z-[2100] w-full outline-none', className)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Overlay>
  </DialogPrimitive.Portal>
))
CustomDialogContent.displayName = DialogPrimitive.Content.displayName

export interface ModalProps {
  open?: boolean
  title?: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  variant?: 'default' | 'destructive'
  closable?: boolean
  closeIcon?: React.ReactNode
  showCancelButton?: boolean
  cancelButtonText?: string
  cancelButtonProps?: React.ComponentProps<typeof Button>
  showSecondaryButton?: boolean
  secondaryButtonText?: string
  secondaryButtonProps?: React.ComponentProps<typeof Button>
  showPrimaryButton?: boolean
  primaryButtonText?: string
  primaryButtonIcon?: React.ReactNode
  primaryButtonProps?: React.ComponentProps<typeof Button>
  hint?: React.ReactNode
  footer?: React.ReactNode
  maskClosable?: boolean
  showOverlay?: boolean
  keyboard?: boolean
  className?: string
  style?: React.CSSProperties
  onCancel?: () => void
  /** Called when the modal is dismissed via X, ESC, or overlay click. Falls back to onCancel. */
  onDismiss?: () => void
  onCancelButton?: () => void
  onOk?: () => Promise<void> | void
  onSecondary?: () => Promise<void> | void
  confirmLoading?: boolean
  secondaryLoading?: boolean
}

const sizeMap = {
  small: 'w-[400px]',
  medium: 'w-[600px]',
  large: 'w-[800px]',
  xlarge: 'w-[1024px]',
} as const

export const Modal: React.FC<ModalProps> = ({
  open = false,
  title,
  subtitle,
  children,
  size = 'small',
  variant = 'default',
  closable = true,
  closeIcon,
  showCancelButton = true,
  cancelButtonText = 'Cancel',
  cancelButtonProps,
  showSecondaryButton = false,
  secondaryButtonText = 'Secondary',
  secondaryButtonProps,
  showPrimaryButton = true,
  primaryButtonText,
  primaryButtonIcon,
  primaryButtonProps,
  hint,
  footer,
  maskClosable = true,
  showOverlay = true,
  keyboard = true,
  className,
  style,
  onCancel,
  onDismiss,
  onCancelButton,
  onOk,
  onSecondary,
  confirmLoading = false,
  secondaryLoading = false,
}) => {
  const [internalConfirmLoading, setInternalConfirmLoading] = React.useState(false)
  const [internalSecondaryLoading, setInternalSecondaryLoading] = React.useState(false)

  const defaultPrimaryText = variant === 'destructive' ? 'Delete' : 'Save'
  const finalPrimaryText = primaryButtonText || defaultPrimaryText

  // Use internal loading states or external ones
  const isConfirmLoading = internalConfirmLoading || confirmLoading
  const isSecondaryLoading = internalSecondaryLoading || secondaryLoading

  const handleOkClick = React.useCallback(async () => {
    if (!onOk || isConfirmLoading || isSecondaryLoading) return

    setInternalConfirmLoading(true)
    try {
      await onOk()
    } finally {
      setInternalConfirmLoading(false)
    }
  }, [onOk, isConfirmLoading, isSecondaryLoading])

  const handleSecondaryClick = React.useCallback(async () => {
    if (!onSecondary || isConfirmLoading || isSecondaryLoading) return

    setInternalSecondaryLoading(true)
    try {
      await onSecondary()
    } finally {
      setInternalSecondaryLoading(false)
    }
  }, [onSecondary, isConfirmLoading, isSecondaryLoading])
  const renderCloseButton = () => {
    if (!closable) return null

    return (
      <Button
        data-testid='modal-close-button'
        variant='ghost'
        size='icon-sm'
        onClick={onDismiss ?? onCancel}
        className='absolute right-4 top-4 z-10'
        aria-label='Close modal'
        disabled={isConfirmLoading || isSecondaryLoading}
      >
        {closeIcon || <XIcon className='size-4' />}
      </Button>
    )
  }

  const renderFooter = () => {
    if (footer !== undefined) {
      return footer
    }

    const buttons = []

    if (showCancelButton) {
      buttons.push(
        <Button
          key='cancel'
          data-testid='modal-cancel-button'
          variant='ghost'
          onClick={onCancelButton || onCancel}
          disabled={isConfirmLoading || isSecondaryLoading}
          {...cancelButtonProps}
        >
          {cancelButtonText}
        </Button>,
      )
    }

    if (showSecondaryButton) {
      buttons.push(
        <Button
          key='secondary'
          data-testid='modal-secondary-button'
          variant='outline'
          onClick={handleSecondaryClick}
          disabled={isSecondaryLoading || isConfirmLoading}
          {...secondaryButtonProps}
        >
          {isSecondaryLoading && <Loader2Icon className='mr-2 size-4 animate-spin' />}
          {secondaryButtonText}
        </Button>,
      )
    }

    if (showPrimaryButton) {
      buttons.push(
        <Button
          key='primary'
          data-testid='modal-primary-button'
          variant={variant}
          onClick={handleOkClick}
          {...primaryButtonProps}
          // Combine with primaryButtonProps.disabled
          disabled={isConfirmLoading || isSecondaryLoading || primaryButtonProps?.disabled}
        >
          {isConfirmLoading ? (
            <Loader2Icon className='mr-2 size-4 animate-spin' />
          ) : (
            primaryButtonIcon && <span className='mr-2 flex items-center'>{primaryButtonIcon}</span>
          )}
          {finalPrimaryText}
        </Button>,
      )
    }

    if (buttons.length === 0) {
      return null
    }

    return (
      <div className='mt-6 flex grow items-center justify-end gap-2'>
        {hint ? <div className='mr-auto'>{hint}</div> : null} {buttons}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && (onDismiss ?? onCancel)?.()}>
      <CustomDialogContent
        data-testid='modal-content'
        showOverlay={showOverlay}
        className={cn(
          sizeMap[size],
          'rounded-lg border-0 bg-main-background-card p-0 shadow-md',
          className,
        )}
        style={style}
        onPointerDownOutside={(e) => {
          if (!maskClosable) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!keyboard) {
            e.preventDefault()
          }
        }}
      >
        <div className='relative flex h-full flex-col p-6'>
          {renderCloseButton()}

          {title && (
            <DialogHeader className='mb-4' data-testid='modal-header'>
              <DialogTitle
                className='text-main-foreground-primary text-heading-small'
                data-testid='modal-title'
              >
                {title}
              </DialogTitle>
              {subtitle ? (
                <DialogDescription
                  className='!mt-1 text-main-foreground-secondary text-body-default'
                  data-testid='modal-subtitle'
                >
                  {subtitle}
                </DialogDescription>
              ) : (
                <DialogDescription className='sr-only'>Modal dialog</DialogDescription>
              )}
            </DialogHeader>
          )}

          {children && (
            <div className='min-h-0 flex-1' data-testid='modal-body'>
              {children}
            </div>
          )}

          <DialogFooter data-testid='modal-footer'>{renderFooter()}</DialogFooter>
        </div>
      </CustomDialogContent>
    </Dialog>
  )
}

Modal.displayName = 'Modal'
